import { Injectable } from '@angular/core';
import { AppUser } from 'src/app/common/models/Utente';
import { Subject, Observable, BehaviorSubject, Subscription, lastValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { PreferencesService } from '../preferences/preferences.service';
import { MemorizzatoreEntitaService } from '../memorizzatoreEntita/memorizzatore-entita.service';
import { Storage } from '@ionic/storage';

/**
 * Service che espone tutti i metodi di interazione con l'unica
 * istanza di AppUser che è la collezione dei dati di login
 * ricevuti dal server su un utente + token di sessione
 * - I componenti leggono i dati utente tramite sottoscrizione all'observable
 * - AppUser non rappresenta le preferenze applicative (vedi PreferencesService)
 * - AppUser può ricevere la modifica del token di sessione o dell'azienda in uso
 */
@Injectable({
  providedIn: 'root'
})
export class UserService extends MemorizzatoreEntitaService<AppUser> {
  /**
   * Emettitore dell'ultimo insieme delle preferenze utente a cui i diversi componenti
   * dell'applicazione si DEVONO iscrivere per avere il valore (aggiornato)
   * 
   * La sottoscrizione avviene tramite metodo getter per controllare l'inizializzazione
   * dello stato alla prima richiesta
   */
  private utenteObservable: Subject<AppUser>;
  private STORAGE_KEY_USERDATA = '__userData';

  private lastUserState: AppUser;
  private internalSubscriptions: Subscription;

  protected getChiaveDB(): string {
    return this.STORAGE_KEY_USERDATA;
  }

  protected verificaUguaglianzaEntita(a: AppUser, b: AppUser): boolean {
    return true; // sovrascrivere sempre
  }

  constructor(private preferenzeService: PreferencesService, storage: Storage) {
    super(storage);
    this.initializeObservables();
  }

  private async initializeObservables() {
    if (!this.utenteObservable) {
      let userData = null;
      const datiUtenteDb = await this.getElencoEntita();
      if (!(datiUtenteDb && datiUtenteDb.length > 0)) {
        console.warn('Nessun dato utente memorizzato precedentemente');
      } else {
        userData = datiUtenteDb[0];
      }
      this.utenteObservable = new BehaviorSubject<AppUser>(userData);
      console.debug('Inizializzato observable dati utente');
      this.internalSubscriptions = this.utenteObservable.subscribe(user => {
        this.lastUserState = user;
      });
    }
  }

  /**
   * Metodo che memorizza i dati dell'utente ed è invocato:
   * - In seguito al login
   * - In seguito ad un refresh del token (con nuovo login)
   * - In seguito all'aggiornamento delle configurazioni (con nuovo login)
   * - Al cambio azienda in uso
   * @param utente 
   */
  async setDatiUtente(utente: AppUser) {
    await this.salvaEntita(utente, true);
    if (!this.utenteObservable) {
      await this.initializeObservables();
    } else {
      // TODO: valutare se sia meglio ritornare una copia difensiva dell'oggetto
      this.utenteObservable.next(utente);
    }
  }

  /**
   * Ottenimento del Observable (Subject in realtà) da cui leggere e ricevere
   * gli aggiornamenti dei dati utente.
   * Asincrono solo alla prima invocazione (pattern singleton: recupero
   * l'unica istanza dal DB solo all'inizializzazione dell'app)
   */
  async getDatiUtenteObservable(): Promise<Observable<AppUser>> {
    if (!this.utenteObservable) {
      await this.initializeObservables();
    }
    return this.utenteObservable;
  }

  /**
   * Ottenimento dei dati utente in questo determinato momento
   * MEGLIO USARE la sottoscrizione all'observable
   */
  async getDatiUtente(): Promise<AppUser> {
    await this.getDatiUtenteObservable();
    return lastValueFrom(this.utenteObservable.pipe(first()));
  }

  /**
   * Rimuove dal DB tutti i dati dell'utente e
   * preferenze applicative collegate
   */
  async logout(): Promise<void> {
    await this.rimuoviEntita();
    await this.preferenzeService.deletePreferencesAfterLogout();
    this.utenteObservable.next(null);
  }

  /**
   * Cambia l'azienda selezionata dall'utente
   * @param aziCdazi 
   */
  async setNuovaAziendaAttiva(aziCdazi: string) {
    const ultimiDatiUtente = await this.getDatiUtente();
    const nuoviDatiUtente: AppUser = (JSON.parse(JSON.stringify(ultimiDatiUtente)));
    nuoviDatiUtente.aziCdazi = aziCdazi;
    await this.setDatiUtente(nuoviDatiUtente);
  }
}
